import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Vote, 
  Users, 
  Clock, 
  Loader2,
  BarChart3,
  Trophy,
  TrendingUp
} from "lucide-react";
import { toast } from "sonner";
import { publicAPI } from "../lib/api";
import { formatDate } from "../lib/utils";
import AnimatedFooter from "../components/AnimatedFooter";

export default function PublicPollResults() {
  const { id } = useParams();
  const [poll, setPoll] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPoll();
    const interval = setInterval(loadPoll, 3000); // Refresh every 3 seconds
    return () => clearInterval(interval);
  }, [id]);

  const loadPoll = async () => {
    try {
      const res = await publicAPI.getPoll(id);
      setPoll(res.data);
    } catch (error) {
      toast.error("Sondage non trouvé");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <Loader2 className="w-12 h-12 text-white animate-spin" />
      </div>
    );
  }

  if (!poll) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <Card className="bg-white/10 backdrop-blur border-white/20">
          <CardContent className="p-8 text-center">
            <Vote className="w-16 h-16 text-white/50 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white">Sondage non trouvé</h2>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalVotes = poll.total_votes || 0;
  const isClosed = poll.status === "closed";
  const sortedOptions = [...(poll.options || [])].sort((a, b) => b.votes - a.votes);
  const maxVotes = sortedOptions.length > 0 ? sortedOptions[0].votes : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          {poll.organizer_logo && (
            <img 
              src={poll.organizer_logo} 
              alt={poll.organizer_name}
              className="h-16 mx-auto mb-4 object-contain"
            />
          )}
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>
            {poll.title}
          </h1>
          <p className="text-white/70">{poll.organizer_name}</p>
          
          <div className="flex items-center justify-center gap-4 mt-4">
            <Badge className={`${isClosed ? "bg-slate-500" : "bg-green-500"} text-white`}>
              {isClosed ? "Terminé" : "En cours"}
            </Badge>
          </div>
        </div>

        {/* Live Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <Card className="bg-white/10 backdrop-blur border-white/20">
            <CardContent className="p-4 text-center">
              <BarChart3 className="w-8 h-8 text-purple-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-white">{totalVotes}</p>
              <p className="text-white/60 text-sm">Votes totaux</p>
            </CardContent>
          </Card>
          
          <Card className="bg-white/10 backdrop-blur border-white/20">
            <CardContent className="p-4 text-center">
              <Vote className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-white">{poll.options?.length || 0}</p>
              <p className="text-white/60 text-sm">Options</p>
            </CardContent>
          </Card>
        </div>

        {/* Results */}
        <Card className="bg-white/10 backdrop-blur border-white/20">
          <CardContent className="p-6">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-400" />
              Résultats en temps réel
            </h3>
            
            <div className="space-y-6">
              {sortedOptions.map((option, index) => {
                const percentage = totalVotes > 0 ? Math.round((option.votes / totalVotes) * 100) : 0;
                const isWinner = index === 0 && option.votes > 0;
                
                return (
                  <motion.div
                    key={option.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {isWinner && (
                          <Trophy className="w-5 h-5 text-yellow-400" />
                        )}
                        <div className="flex items-center gap-3">
                          {option.image_url && (
                            <img 
                              src={option.image_url} 
                              alt={option.title}
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                          )}
                          <div>
                            <p className={`font-medium ${isWinner ? "text-yellow-300" : "text-white"}`}>
                              {option.title}
                            </p>
                            {option.description && (
                              <p className="text-white/50 text-sm">{option.description}</p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-2xl font-bold ${isWinner ? "text-yellow-300" : "text-white"}`}>
                          {percentage}%
                        </p>
                        <p className="text-white/50 text-sm">{option.votes} votes</p>
                      </div>
                    </div>
                    
                    <div className="relative">
                      <Progress 
                        value={percentage} 
                        className="h-3 bg-white/10"
                      />
                      <motion.div
                        className={`absolute inset-y-0 left-0 rounded-full ${
                          isWinner 
                            ? "bg-gradient-to-r from-yellow-400 to-amber-500" 
                            : "bg-gradient-to-r from-purple-500 to-blue-500"
                        }`}
                        initial={{ width: 0 }}
                        animate={{ width: `${percentage}%` }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                        style={{ height: "100%" }}
                      />
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {totalVotes === 0 && (
              <div className="text-center py-8">
                <Vote className="w-12 h-12 text-white/30 mx-auto mb-4" />
                <p className="text-white/60">Aucun vote pour le moment</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Auto-refresh indicator */}
        {!isClosed && (
          <div className="text-center mt-6">
            <motion.div
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ repeat: Infinity, duration: 2 }}
              className="flex items-center justify-center gap-2 text-white/50 text-sm"
            >
              <Clock className="w-4 h-4" />
              Mise à jour automatique toutes les 3 secondes
            </motion.div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8">
          <AnimatedFooter variant="transparent" />
        </div>
      </div>
    </div>
  );
}
